/**
 * Auto-playing product-video teaser, pinned to the bottom-left corner of the
 * product image as you scroll.
 *
 * Shows a static poster image instantly. To start playing quickly without
 * hurting page load, three things happen in parallel the moment the element
 * connects: (1) on non-Safari browsers the hls.js library is pre-warmed (its
 * download overlaps page load instead of gating the first frame), (2) the
 * video is injected as soon as the DOM is ready, and (3) hls.js starts at the
 * lowest rendition so the first segment is tiny and arrives fast. hls.js then
 * caps quality to the box size (480p for the small teaser, stepping up when
 * expanded), keeping bandwidth low. Safari/iOS plays the HLS stream natively.
 *
 * Positioning: this element is `position: fixed`, which by default holds it a
 * constant distance from the *screen's* bottom edge regardless of where the
 * product image currently is. That looks right only while the image fills
 * the screen; once the image (and thumbnails) end higher up — e.g. on a
 * wider viewport, or near the bottom of a tall image — a purely
 * screen-relative box keeps sitting at the same spot and ends up below the
 * image, overlapping the thumbnail strip or the add-to-cart bar.
 *
 * #syncPosition() fixes this WITHOUT moving the element in the DOM (an
 * earlier attempt did that — re-parenting it into the image container so CSS
 * `position: absolute` could work — and it caused the whole page to flicker
 * and jump on scroll, because relocating a node changes layout above the
 * viewport and triggers the browser's scroll-anchoring correction). Instead,
 * on every scroll/resize we read the image container's current
 * getBoundingClientRect() and write a plain `top` value to this element's own
 * inline style — a read then a write to an element that's already
 * `position: fixed`, so it never touches layout elsewhere and can't trigger
 * that bug. The math: normally sit `margin + boxHeight` above the screen
 * bottom, but never let the box's bottom go past the image container's own
 * bottom edge (clamped via Math.min), and never overlap whichever "Add to
 * cart" bar (native or Rosio's custom floating one) happens to be showing.
 * An IntersectionObserver separately fades the box out once the image
 * scrolls out of view entirely.
 */
class ProductVideoPopup extends HTMLElement {
  #videoInjected = false;
  #galleryObserver = null;
  #gallery = null;
  #ticking = false;

  connectedCallback() {
    this.media = this.querySelector('[data-video-media]');
    this.poster = this.querySelector('[data-video-poster]');
    this.template = this.querySelector('template');
    this.closeButton = this.querySelector('[data-video-close]');
    this.frame = this.querySelector('[data-video-frame]');

    this.media?.addEventListener('click', this.#handleMediaClick);
    this.media?.addEventListener('keydown', this.#handleMediaKeydown);
    this.closeButton?.addEventListener('click', this.#dismiss);

    // Pre-warm hls.js right away so the first frame isn't gated on a serial
    // library download. Only on browsers that need it (not Safari/iOS).
    const tmplVideo = this.template?.content.querySelector('video');
    const libUrl = tmplVideo?.dataset.hlsLib;
    if (libUrl && tmplVideo?.dataset.hls && !ProductVideoPopup.nativeHls()) {
      ProductVideoPopup.loadHlsLib(libUrl).catch(() => {});
    }

    this.#watchGallery();
    this.#scheduleVideoLoad();
  }

  disconnectedCallback() {
    this.media?.removeEventListener('click', this.#handleMediaClick);
    this.media?.removeEventListener('keydown', this.#handleMediaKeydown);
    this.closeButton?.removeEventListener('click', this.#dismiss);
    this.#galleryObserver?.disconnect();
    window.removeEventListener('scroll', this.#requestSync);
    window.removeEventListener('resize', this.#requestSync);
  }

  #watchGallery() {
    const wrapper = this.closest('.product-information')?.querySelector('.product-information__media');
    // slideshow-container wraps just the photo itself, not the thumbnail
    // strip that renders as its sibling below — that's the boundary we want
    // the teaser clamped to. Falls back to the outer wrapper for the (rarer)
    // grid media layout, which has no separate thumbnail strip to avoid.
    const gallery = wrapper?.querySelector('slideshow-container') || wrapper;
    this.#gallery = gallery;
    if (!gallery) return;

    // Fade out once the image is fully scrolled out of view.
    if (typeof IntersectionObserver !== 'undefined') {
      this.#galleryObserver = new IntersectionObserver(
        ([entry]) => {
          // Before the page finishes laying out (images/fonts still
          // loading), the gallery can briefly report zero height — reading
          // as "not visible" and fading the teaser out for a moment, then
          // correcting itself once the real layout settles. Ignore those
          // zero-size readings; only act once the gallery has a real box.
          if (entry.boundingClientRect.height === 0) return;
          this.classList.toggle('product-video-popup--hidden', !entry.isIntersecting);
        },
        { threshold: 0 }
      );
      this.#galleryObserver.observe(gallery);
    }

    // Track the image's current on-screen position while scrolling.
    window.addEventListener('scroll', this.#requestSync, { passive: true });
    window.addEventListener('resize', this.#requestSync, { passive: true });
    this.#requestSync();
  }

  #requestSync = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#syncPosition();
      this.#ticking = false;
    });
  };

  #syncPosition() {
    if (!this.frame || !this.#gallery) return;

    const galleryRect = this.#gallery.getBoundingClientRect();
    const isMobile = window.innerWidth <= 749;
    const margin = isMobile ? 12 : 16;
    // Computed to match the CSS exactly (see stylesheet), rather than read
    // from the DOM with offsetHeight — mid-transition (expanding/collapsing
    // on click) offsetHeight reports a shifting, unreliable number, which
    // made the box visibly jump. Plain math always gives the true target
    // size instantly, so `top` can transition smoothly in step with the
    // width/height CSS transition instead of correcting itself afterward.
    const boxHeight = this.#getBoxHeight(isMobile);

    // Clear whichever "Add to cart" bar (native or Rosio's custom floating
    // one) is currently showing at the bottom of the screen.
    const barVisible =
      document.querySelector('.floating-quick-add[data-visible="true"]') ||
      document.querySelector('.sticky-add-to-cart__bar[data-stuck="true"]');
    const barClearance = barVisible ? 74 : 0; // ~button height + padding

    // Normal position: a fixed margin above the bottom of the screen.
    const screenBottomTop = window.innerHeight - margin - barClearance - boxHeight;
    // Never let the box sit lower than the image's own bottom edge.
    const imageBottomTop = galleryRect.bottom - margin - boxHeight;

    const top = Math.min(screenBottomTop, imageBottomTop);
    this.frame.style.top = `${top}px`;
  }

  /**
   * Mirrors the CSS exactly (see stylesheet in product-video-popup.liquid):
   *   collapsed — 160px desktop / 128px mobile
   *   expanded  — min(70vh, 440px) desktop / min(60vh, 380px) mobile
   */
  #getBoxHeight(isMobile) {
    const expanded = this.hasAttribute('data-expanded');
    if (!expanded) return isMobile ? 128 : 160;
    return isMobile ? Math.min(window.innerHeight * 0.6, 380) : Math.min(window.innerHeight * 0.7, 440);
  }

  #scheduleVideoLoad() {
    const load = () => this.#loadVideo();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', load, { once: true });
    } else {
      load();
    }
  }

  #loadVideo() {
    if (this.#videoInjected || !this.template || !this.media) return;
    this.#videoInjected = true;

    const content = this.template.content.cloneNode(true);
    this.media.appendChild(content);

    const video = this.media.querySelector('video');
    if (!video) {
      this.poster?.remove();
      return;
    }

    // Videos cloned out of a <template> can lose their muted/playsinline
    // state in WebKit, which makes iOS refuse autoplay. Re-assert them.
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;

    // Drop the poster only once playback has actually started rendering
    // frames — NOT on 'loadeddata', which just means enough data has
    // buffered and can fire well before the first frame is on screen (e.g.
    // while hls.js is still attaching, or play() is still pending). Removing
    // the poster on 'loadeddata' left a brief gap between the poster
    // disappearing and real video frames appearing, showing as a washed-out/
    // semi-transparent moment during load.
    video.addEventListener('playing', () => this.poster?.remove(), { once: true });

    this.#attachSource(video);
  }

  #attachSource(video) {
    const hlsUrl = video.dataset.hls;
    const mp4Url = video.dataset.mp4;
    const libUrl = video.dataset.hlsLib;

    const play = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    const useMp4 = () => {
      if (mp4Url) {
        video.src = mp4Url;
        play();
      }
    };

    // No HLS available (e.g. external video already has its own <source>).
    if (!hlsUrl) {
      play();
      return;
    }

    // Safari / iOS — native HLS.
    if (ProductVideoPopup.nativeHls()) {
      video.src = hlsUrl;
      play();
      return;
    }

    // Everyone else — hls.js (pre-warmed above), then attach.
    if (!libUrl) {
      useMp4();
      return;
    }
    ProductVideoPopup.loadHlsLib(libUrl)
      .then(() => {
        if (window.Hls && window.Hls.isSupported()) {
          const hls = new window.Hls({
            capLevelToPlayerSize: true, // match rendition to the small box
            startLevel: 0, // start at the lowest rendition → tiny first segment
            maxBufferLength: 10, // don't hoard bandwidth on a looping teaser
          });
          hls.loadSource(hlsUrl);
          hls.attachMedia(video);
          hls.on(window.Hls.Events.MANIFEST_PARSED, play);
          hls.on(window.Hls.Events.ERROR, (_e, data) => {
            if (data && data.fatal) useMp4();
          });
        } else {
          useMp4();
        }
      })
      .catch(useMp4);
  }

  static nativeHls() {
    if (ProductVideoPopup._nativeHls === undefined) {
      ProductVideoPopup._nativeHls =
        document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';
    }
    return ProductVideoPopup._nativeHls;
  }

  static loadHlsLib(url) {
    if (window.Hls) return Promise.resolve();
    if (ProductVideoPopup._hlsLibPromise) return ProductVideoPopup._hlsLibPromise;
    ProductVideoPopup._hlsLibPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return ProductVideoPopup._hlsLibPromise;
  }

  #handleMediaClick = () => {
    this.toggleAttribute('data-expanded'); const video = this.querySelector('video'); if (video) { video.muted = true; }
    // `top` has no CSS transition by default (see stylesheet) — it updates
    // instantly on every scroll event so tracking the image never lags or
    // overshoots. Expand/collapse is the one case that wants `top` to ease
    // smoothly alongside width/height, so add that transition just for this
    // one update, then remove it — otherwise the next scroll-driven update
    // would also ease in and cause the same lag/bounce we're avoiding.
    if (this.frame) {
      this.frame.style.transition = 'width 0.25s var(--ease-out-quad), height 0.25s var(--ease-out-quad), top 0.25s var(--ease-out-quad)';
    }
    this.#syncPosition();
    window.setTimeout(() => {
      if (this.frame) this.frame.style.transition = '';
    }, 300);
  };

  #handleMediaKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#handleMediaClick();
    }
  };

  #dismiss = (event) => {
    event.stopPropagation();
    this.remove();
  };
}

if (!customElements.get('product-video-popup')) {
  customElements.define('product-video-popup', ProductVideoPopup);
}
